/**
 * 電波スキャナー（章 4）。
 *
 * チャンネルごとの電波の混み具合を横棒グラフで見せる、測定用の道具。
 * 設定を変える機能は持たない（変更は AP の管理画面の仕事）。
 *
 * 【厳守】
 * - 表示する値はすべて sim/wireless.ts の計算結果。数値をここに書かない。
 * - **おすすめチャンネルは recommendChannel() の計算結果を出す。**
 *   会話文にもデータにも答えを書かない。状況が変われば答えも変わる。
 */

import type { DeviceId, NetworkWorld } from '../types/network';
import {
  recommendChannel,
  scanChannels,
  wirelessQuality,
  type ChannelScan,
  type WirelessQuality,
} from '../sim/wireless';
import { button, clear, el } from './dom';

export interface ScannerHooks {
  onClose: () => void;
}

export class WirelessScanner {
  readonly root: HTMLElement;

  private readonly pickerNode: HTMLElement;
  private readonly summaryNode: HTMLElement;
  private readonly chartNode: HTMLElement;
  private readonly adviceNode: HTMLElement;

  private world: NetworkWorld | null = null;
  private flags: Record<string, boolean> = {};
  private apId: DeviceId = '';

  constructor(private readonly hooks: ScannerHooks) {
    this.pickerNode = el('div', { class: 'scan-picker' });
    this.summaryNode = el('div', { class: 'scan-summary' });
    this.chartNode = el('div', { class: 'scan-chart' });
    this.adviceNode = el('div', { class: 'scan-advice' });

    this.root = el('div', { class: 'scan-layer', hidden: true }, [
      el('div', { class: 'scan-window' }, [
        el('div', { class: 'scan-head' }, [
          el('span', { class: 'scan-title', text: '電波スキャナー' }),
          button('閉じる (R / Esc)', () => this.hooks.onClose(), 'btn btn-ghost btn-small'),
        ]),
        this.pickerNode,
        this.summaryNode,
        this.chartNode,
        this.adviceNode,
        el('div', { class: 'scan-legend' }, [
          legendItem('own-ap', 'このアクセスポイント'),
          legendItem('peer-ap', '自社の別のアクセスポイント'),
          legendItem('interference', '外部の干渉源'),
        ]),
      ]),
    ]);
  }

  get isOpen(): boolean {
    return !this.root.hidden;
  }

  get currentApId(): DeviceId {
    return this.apId;
  }

  /** 無線設定を持つ機器の一覧（開ける対象） */
  static accessPointIds(world: NetworkWorld): DeviceId[] {
    return Object.keys(world.devices).filter((id) => !!world.devices[id].wireless);
  }

  open(world: NetworkWorld, apId: DeviceId, flags: Record<string, boolean>): void {
    this.world = world;
    this.flags = flags;
    const candidates = WirelessScanner.accessPointIds(world);
    this.apId = candidates.includes(apId) ? apId : (candidates[0] ?? '');
    this.root.hidden = false;
    this.render();
  }

  close(): void {
    this.root.hidden = true;
  }

  render(): void {
    const world = this.world;
    clear(this.pickerNode);
    clear(this.summaryNode);
    clear(this.chartNode);
    clear(this.adviceNode);
    if (!world) return;

    const candidates = WirelessScanner.accessPointIds(world);
    if (candidates.length === 0 || !this.apId) {
      this.summaryNode.append(
        el('p', {
          class: 'scan-empty',
          text: '測定できる無線アクセスポイントが見つかりません。',
        }),
      );
      return;
    }

    this.renderPicker(world, candidates);

    const quality = wirelessQuality(world, this.apId, this.flags);
    if (!quality) {
      this.summaryNode.append(
        el('p', { class: 'scan-empty', text: 'この機器は無線設定を持っていません。' }),
      );
      return;
    }

    this.renderSummary(quality);
    this.renderChart(scanChannels(world, this.apId, this.flags), quality);
    this.renderAdvice(world, quality);
  }

  private renderPicker(world: NetworkWorld, candidates: DeviceId[]): void {
    if (candidates.length <= 1) return;
    this.pickerNode.append(el('span', { class: 'scan-picker-label', text: '測定するAP' }));
    for (const id of candidates) {
      const device = world.devices[id];
      this.pickerNode.append(
        button(
          device.label,
          () => {
            this.apId = id;
            this.render();
          },
          id === this.apId ? 'btn btn-small scan-pick active' : 'btn btn-small scan-pick',
        ),
      );
    }
  }

  private renderSummary(quality: WirelessQuality): void {
    const percent = Math.round(quality.interferenceRatio * 100);
    this.summaryNode.append(
      summaryItem('SSID', quality.ssid),
      summaryItem('周波数帯', quality.band),
      summaryItem('いま使っているチャンネル', `${quality.channel}ch`, 'current'),
      summaryItem('規格上の速度', `${quality.nominalMbps} Mbps`),
      summaryItem(
        '実効速度',
        `${quality.effectiveMbps} Mbps`,
        quality.effectiveMbps < quality.nominalMbps / 2 ? 'bad' : 'good',
      ),
      summaryItem('干渉率', `${percent} %`, percent >= 50 ? 'bad' : percent > 0 ? 'warn' : 'good'),
    );
  }

  private renderChart(scans: ChannelScan[], quality: WirelessQuality): void {
    this.chartNode.append(
      el('div', { class: 'scan-chart-title', text: `チャンネル別の電波の混み具合（${quality.band}）` }),
    );
    for (const scan of scans) {
      const bar = el('div', { class: 'scan-bar' });
      for (const entry of scan.entries) {
        if (entry.strength <= 0) continue;
        const segment = el('div', { class: `scan-seg scan-seg-${entry.kind}` });
        segment.style.width = `${Math.min(100, entry.strength)}%`;
        segment.title = `${entry.label}（強さ ${entry.strength}）`;
        bar.append(segment);
      }
      if (scan.entries.length === 0) {
        bar.append(el('div', { class: 'scan-seg scan-seg-empty' }));
      }

      const labels = scan.entries.map((entry) => entry.label).join(' / ');
      this.chartNode.append(
        el('div', { class: scan.current ? 'scan-row current' : 'scan-row' }, [
          el('span', { class: 'scan-ch', text: `${scan.channel}ch` }),
          bar,
          el('span', {
            class: 'scan-note',
            text: scan.current ? `使用中 — ${labels || '他に電波なし'}` : labels,
          }),
        ]),
      );
    }
  }

  private renderAdvice(world: NetworkWorld, quality: WirelessQuality): void {
    // ここが「答え」を出す唯一の場所。データではなく計算から出す
    const recommended = recommendChannel(world, this.apId, this.flags);
    const same = recommended === quality.channel;
    this.adviceNode.append(
      el('div', { class: 'scan-advice-head', text: 'スキャナーの見立て' }),
      el('div', { class: 'scan-advice-body' }, [
        el('span', { class: 'scan-advice-ch', text: `おすすめ: ${recommended}ch` }),
        el('span', {
          class: 'scan-advice-text',
          text: same
            ? '（いまのチャンネルより空いているものは、この帯域には見つかりませんでした）'
            : `（いまの ${quality.channel}ch より空いています）`,
        }),
      ]),
    );

    const broadband = quality.sources.filter((source) => source.overlap >= 0.8 && source.channel > 0);
    if (same && quality.interferenceRatio > 0 && broadband.length > 0) {
      this.adviceNode.append(
        el('p', {
          class: 'scan-advice-hint',
          text: 'この帯域は全体が塞がっています。チャンネルを変えても逃げられません。',
        }),
      );
    }
  }
}

function summaryItem(label: string, value: string, tone?: string): HTMLElement {
  return el('div', { class: 'scan-item' }, [
    el('span', { class: 'scan-item-label', text: label }),
    el('span', { class: tone ? `scan-item-value ${tone}` : 'scan-item-value', text: value }),
  ]);
}

function legendItem(kind: string, label: string): HTMLElement {
  return el('span', { class: 'scan-legend-item' }, [
    el('span', { class: `scan-swatch scan-seg-${kind}` }),
    el('span', { text: label }),
  ]);
}
